import { asString } from "../document/validate.ts";

/**
 * The files a world manifest names beside its chunks (P1.4-T14): the version's contract and the game's own files,
 * each a plain relative path inside the version directory, checked here for `buildManifest` and `deserialiseManifest`.
 */

/**
 * The files a version names beside its chunks: its contract and the game's own files, each a path in the version
 * directory.
 */
export interface NamedFiles {
  readonly contract: string;
  readonly files: readonly string[];
}

const PATH_SEGMENT = /^[A-Za-z0-9_-][A-Za-z0-9._-]*$/;
const MANIFEST_FILE = "manifest.json";

/**
 * Checks one path a manifest names beside its chunks: relative, slash-separated segments of `A-Za-z0-9._-`, none
 * starting with `.`, so it can neither climb out of the version directory nor name a hidden entry, and not the
 * manifest itself.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The path.
 * @throws Error naming `path` when the value is not such a path.
 */
function readNamedPath(value: unknown, path: string): string {
  const named = asString(value, path);
  if (named === MANIFEST_FILE || !named.split("/").every((segment) => PATH_SEGMENT.test(segment))) {
    throw new Error(`${path} ${JSON.stringify(named)} is not a plain relative path inside the version`);
  }
  return named;
}

/**
 * Checks the files a version names and puts the game's files in path order.
 *
 * @param contract The contract's path.
 * @param files The game's files' paths.
 * @returns The contract's path and the files in path order.
 * @throws Error when a path is not a plain relative path inside the version, a file is named twice, or a file is
 *   the contract.
 */
export function readNamedFiles(contract: unknown, files: readonly unknown[]): NamedFiles {
  const contractPath = readNamedPath(contract, "contract");
  const seen = new Set<string>();
  const paths = files.map((file, i) => {
    const named = readNamedPath(file, `files[${i}]`);
    if (named === contractPath) {
      throw new Error(`files[${i}] ${named} is the contract`);
    }
    if (seen.has(named)) {
      throw new Error(`files names ${named} twice`);
    }
    seen.add(named);
    return named;
  });
  return { contract: contractPath, files: paths.sort() };
}

/**
 * The manifest fields for the files a version names, empty when it names none.
 *
 * @param named The files, or undefined.
 * @returns `contract`, and `files` when the game wrote any.
 */
export function namedFields(named: NamedFiles | undefined): { contract?: string; files?: readonly string[] } {
  if (named === undefined) {
    return {};
  }
  return { contract: named.contract, ...(named.files.length === 0 ? {} : { files: named.files }) };
}
